import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from './ui/card';
import { Check, Lock, Sparkles, Shield, Heart, BookOpen, Trophy, Gift, Star, BadgeCheck, ArrowLeft, CheckCircle2, CircleDashed, Ticket } from 'lucide-react';
import {
  fetchChild,
  getPaymentQuote,
  createCheckoutSession,
  redeemPromoCode,
  FIRST_CHILD_PRICE_CENTS,
  ADDITIONAL_CHILD_PRICE_CENTS,
} from '../services/api';
import ekoIcon from '../assets/eko-_transparent-removebg-preview.png';
import { trackEvent } from '../services/metaPixel';
import { PENDING_PURCHASE_KEY, PURCHASE_CONTENT_ID } from './PaymentResult';

interface PaymentChild {
  id: string;
  name: string;
  grade: string;
  paymentStatus: 'paid' | 'unpaid';
}

const AVATAR_COLORS = ['bg-[#F76C8A]', 'bg-[#4C9E8F]', 'bg-[#8E7BFF]', 'bg-[#F7B26A]'];
const AVATAR_ICONS = [Star, BookOpen, Trophy, Gift];

const BENEFITS = [
  { icon: BookOpen, text: 'All four activities for each child' },
  { icon: Sparkles, text: 'A Learning Snapshot for each child, ready right after they finish' },
  { icon: Heart, text: 'Practical tips you can use that same week' },
  { icon: Trophy, text: 'A shareable report you can send to a teacher or tutor' },
];

const formatUsd = (cents: number) => `$${(cents / 100) % 1 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`;

export const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectChildId = (location.state as any)?.preselectChildId as string | undefined;

  const [children, setChildren] = useState<PaymentChild[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [serverPrices, setServerPrices] = useState<Record<string, number> | null>(null);

  // Promo code redemption state — the target child is picked from the
  // already-selected children in the grid, so there's no separate dropdown.
  const [promoCode, setPromoCode] = useState('');
  const [promoTargetId, setPromoTargetId] = useState<string>('');
  const [promoRedeeming, setPromoRedeeming] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');


  const paidCount = children.filter((c) => c.paymentStatus === 'paid').length;
  const unpaidChildren = children.filter((c) => c.paymentStatus === 'unpaid');
  const paidChildren = children.filter((c) => c.paymentStatus === 'paid');
  const allUnpaidSelected = unpaidChildren.length > 0 && unpaidChildren.every((c) => selectedIds.includes(c.id));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await fetchChild();
        if (cancelled) return;
        if (Array.isArray(data)) {
          const mapped: PaymentChild[] = data.map((child: any) => ({
            id: child.child_id,
            name: child.name,
            grade: child.grade,
            paymentStatus: child.payment_status === 'paid' ? 'paid' : 'unpaid',
          }));
          setChildren(mapped);
          const unpaidIds = mapped.filter((c) => c.paymentStatus === 'unpaid').map((c) => c.id);
          setSelectedIds(unpaidIds);
        } else {
          setError('Unexpected response from server. Please try again later.');
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Failed to load profiles. Please try again.');
      }
      if (!cancelled) setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [preselectChildId]);

  const localPrices = useMemo(() => {
    const prices: Record<string, number> = {};
    let count = paidCount;
    for (const child of unpaidChildren) {
      if (!selectedIds.includes(child.id)) continue;
      prices[child.id] = count === 0 ? FIRST_CHILD_PRICE_CENTS : ADDITIONAL_CHILD_PRICE_CENTS;
      count += 1;
    }
    return prices;
  }, [unpaidChildren, selectedIds, paidCount]);

  useEffect(() => {
    let cancelled = false;
    if (selectedIds.length === 0) {
      setServerPrices(null);
      return;
    }
    getPaymentQuote(selectedIds)
      .then((quote) => {
        if (cancelled) return;
        const prices: Record<string, number> = {};
        quote.items.forEach((item) => {
          prices[item.child_id] = item.amount_cents;
        });
        setServerPrices(prices);
      })
      .catch(() => {
        if (!cancelled) setServerPrices(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIds]);

  const prices = serverPrices ?? localPrices;
  const selectedChildren = unpaidChildren.filter((c) => selectedIds.includes(c.id));
  const totalCents = selectedChildren.reduce((sum, c) => sum + (prices[c.id] ?? 0), 0);

  const toggleChild = (childId: string) => {
    setSelectedIds((prev) =>
      prev.includes(childId) ? prev.filter((id) => id !== childId) : [...prev, childId]
    );
  };

  const selectAll = () => setSelectedIds(unpaidChildren.map((c) => c.id));
  const selectNone = () => setSelectedIds([]);

  // Keep the promo-code target within the currently selected children.
  useEffect(() => {
    setPromoTargetId((prev) => {
      if (prev && selectedIds.includes(prev)) return prev;
      return selectedIds.length > 0 ? selectedIds[0] : '';
    });
  }, [selectedIds]);

  const handleUnlock = useCallback(async () => {
    if (selectedIds.length === 0 || isProcessing) return;
    setIsProcessing(true);
    setError('');
    try {
      const session = await createCheckoutSession(selectedIds);
      sessionStorage.setItem('dpp-payment-id', session.payment_id);
      // Stripe hosts checkout off-site and the status endpoint only returns a
      // state, so the order value has to be carried across the round trip for
      // the Purchase event to report real revenue.
      sessionStorage.setItem(
        PENDING_PURCHASE_KEY,
        JSON.stringify({
          paymentId: session.payment_id,
          valueCents: totalCents,
          numItems: selectedIds.length,
        })
      );
      // Order value and a count of profiles only. The child ids stay out of
      // this on purpose — they identify individual children, and nothing that
      // identifies a child is sent to Meta.
      trackEvent('InitiateCheckout', {
        value: totalCents / 100,
        currency: 'USD',
        num_items: selectedIds.length,
        content_ids: [PURCHASE_CONTENT_ID],
        content_type: 'product',
      });
      window.location.href = session.checkout_url;
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Could not start checkout. Please try again.');
      setIsProcessing(false);
    }
  }, [selectedIds, isProcessing, totalCents]);

  const handleRedeemPromo = useCallback(async () => {
    if (!promoCode.trim() || !promoTargetId || promoRedeeming) return;
    setPromoRedeeming(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const result = await redeemPromoCode(promoCode.trim(), promoTargetId);
      setPromoSuccess(`${result.child_name || 'Child'} unlocked! You can now start their assessments.`);
      setPromoCode('');
      // Reload children so the newly unlocked child shows as paid.
      const data = await fetchChild();
      if (Array.isArray(data)) {
        const mapped: PaymentChild[] = data.map((child: any) => ({
          id: child.child_id,
          name: child.name,
          grade: child.grade,
          paymentStatus: child.payment_status === 'paid' ? 'paid' : 'unpaid',
        }));
        setChildren(mapped);
        const unpaidIds = mapped.filter((c) => c.paymentStatus === 'unpaid').map((c) => c.id);
        setSelectedIds(unpaidIds);
      }
    } catch (err: any) {
      setPromoError(err?.response?.data?.detail || err?.message || 'Could not redeem promo code. Please check the code and try again.');
    } finally {
      setPromoRedeeming(false);
    }
  }, [promoCode, promoTargetId, promoRedeeming]);

  const ctaLabel = useMemo(() => {
    if (selectedChildren.length === 0) return 'Select a child to unlock';
    const names =
      selectedChildren.length === 1
        ? `${selectedChildren[0].name}'s profile`
        : selectedChildren.length === 2
        ? `${selectedChildren[0].name} and ${selectedChildren[1].name}'s profiles`
        : `${selectedChildren.length} profiles`;
    return `Unlock ${names} — ${formatUsd(totalCents)}`;
  }, [selectedChildren, totalCents]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3EEE8' }}>
        <div className="text-center">
          <img src={ekoIcon} alt="Eko mascot" className="w-20 h-20 mx-auto mb-4 animate-gentle-bounce object-contain" />
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#F7B26A] border-t-transparent"></div>
          <p className="mt-4 text-lg text-[#1F224B] font-semibold">Getting things ready...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 px-4 sm:py-8" style={{ backgroundColor: '#F3EEE8' }}>
      {/* Decorative background blobs */}
      <div className="fixed -top-10 -left-10 w-72 h-48 bg-gradient-to-r from-[#FFF2E6] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />
      <div className="fixed top-1/2 -right-16 w-72 h-48 bg-gradient-to-r from-[#EDE9FF] to-[#FFF0F6] rounded-full opacity-50 blur-3xl pointer-events-none -z-10" />

      <div className="max-w-6xl mx-auto">
        {/* Header bar matching dashboard style */}
        <header className="w-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white py-5 px-6 rounded-2xl shadow-md mb-6 relative">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/app')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2 transition-colors text-sm font-semibold"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Unlock Assessments
            </h1>
            <div className="w-16" />
          </div>
        </header>

        {unpaidChildren.length === 0 ? (
          <Card className="p-8 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl text-center max-w-md mx-auto">
            <img src={ekoIcon} alt="Eko mascot" className="w-24 h-24 mx-auto mb-4 animate-gentle-float object-contain" />
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#1F224B] mb-3">
              {children.length === 0 ? 'No profiles yet!' : 'All profiles are unlocked!'}
            </h3>
            <p className="text-sm text-[#6B6FA3] mb-6 max-w-sm mx-auto">
              {children.length === 0
                ? "Add your child's profile from the dashboard first, then come back to unlock their assessments."
                : 'Every child can take their assessments now. Enjoy discovering their learning journey!'}
            </p>
            <button
              onClick={() => navigate('/app')}
              className="w-full h-14 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 transition-all shadow-lg btn-bouncy"
            >
              Back to Dashboard
            </button>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-5 gap-5">
            {/* LEFT COLUMN — Child selection (wider, 3/5) */}
            <div className="lg:col-span-3 space-y-5">
              {/* Hero / intro card with eko */}
              <Card className="p-5 sm:p-6 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl">
                <div className="flex items-center gap-4">
                  <img
                    src={ekoIcon}
                    alt="Eko mascot"
                    className="w-16 h-16 sm:w-20 sm:h-20 flex-none object-contain animate-gentle-bounce"
                  />
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-[#1F224B] mb-1">
                      One small step to understanding your kids.
                    </h2>
                    <p className="text-[#6B6FA3] text-sm">
                      No subscription, no hidden fees, no second payment later.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Already unlocked children (if any) */}
              {paidChildren.length > 0 && (
                <Card className="p-4 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-md">
                  <h3 className="text-sm font-bold text-[#1F224B] mb-3 flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-green-600" />
                    Already unlocked
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {paidChildren.map((child, idx) => {
                      const AvatarIcon = AVATAR_ICONS[child.name.charCodeAt(0) % 4] || Star;
                      return (
                        <span
                          key={child.id}
                          className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 text-sm font-medium text-green-700"
                        >
                          <div className={`w-6 h-6 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} flex items-center justify-center text-white`}>
                            <AvatarIcon className="w-3.5 h-3.5" />
                          </div>
                          {child.name}
                        </span>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Child selection card */}
              <Card className="p-5 sm:p-6 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#8E7BFF]" />
                    <h3 className="text-lg font-bold text-[#1F224B]">Choose profiles to unlock</h3>
                  </div>
                  {/* Select all / none */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAll}
                      disabled={allUnpaidSelected}
                      className={`flex items-center gap-1.5 text-sm font-semibold rounded-xl px-3 py-1.5 transition-all ${
                        allUnpaidSelected
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-[#F5F3FF] text-[#8E7BFF] hover:bg-[#EDE9FF] border border-[#8E7BFF]/20'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Select all
                    </button>
                    <button
                      onClick={selectNone}
                      disabled={selectedIds.length === 0}
                      className={`flex items-center gap-1.5 text-sm font-semibold rounded-xl px-3 py-1.5 transition-all ${
                        selectedIds.length === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-[#6B6FA3] hover:bg-gray-200'
                      }`}
                    >
                      <CircleDashed className="w-4 h-4" />
                      Clear
                    </button>
                  </div>
                </div>

                <p className="text-sm text-[#6B6FA3] mb-4">
                  The first child is <span className="font-bold text-[#1F224B]">{formatUsd(FIRST_CHILD_PRICE_CENTS)}</span>,
                  and each additional child is just <span className="font-bold text-[#1F224B]">{formatUsd(ADDITIONAL_CHILD_PRICE_CENTS)}</span>.
                </p>

                {/* Child rows — grid to use horizontal space */}
                <div className="grid sm:grid-cols-2 gap-3">
                  {unpaidChildren.map((child, idx) => {
                    const isSelected = selectedIds.includes(child.id);
                    const price = prices[child.id];
                    const isFirstPriced = isSelected && price === FIRST_CHILD_PRICE_CENTS;
                    const AvatarIcon = AVATAR_ICONS[child.name.charCodeAt(0) % 4] || Star;
                    return (
                      <button
                        key={child.id}
                        onClick={() => toggleChild(child.id)}
                        className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all text-left ${
                          isSelected
                            ? 'bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] border-[#F7B26A] shadow-md'
                            : 'bg-gray-50 border-transparent opacity-60 hover:opacity-80'
                        }`}
                        aria-pressed={isSelected}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-11 h-11 flex-none rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} flex items-center justify-center text-white shadow-sm`}
                          >
                            <AvatarIcon className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-[#1F224B] truncate">{child.name}</div>
                            <div className="text-sm text-[#6B6FA3] truncate">Grade {child.grade}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-none">
                          <span className="font-bold text-[#1F224B] text-sm">
                            {isSelected ? (isFirstPriced ? formatUsd(price) : `+${formatUsd(price ?? ADDITIONAL_CHILD_PRICE_CENTS)}`) : ''}
                          </span>
                          <span
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected ? 'bg-[#4C9E8F] border-[#4C9E8F]' : 'border-gray-300'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Card>
            </div>

            {/* RIGHT COLUMN — Summary, benefits, CTA (sticky, 2/5) */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-6 space-y-5">
                {/* Order summary */}
                <Card className="p-5 sm:p-6 rounded-3xl bg-white/95 backdrop-blur border-4 shadow-xl">
                  <h3 className="text-lg font-bold text-[#1F224B] mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#F7B26A]" />
                    Order Summary
                  </h3>

                  {selectedChildren.length === 0 ? (
                    <div className="text-center py-6">
                      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                        <Lock className="w-7 h-7 text-gray-400" />
                      </div>
                      <p className="text-sm text-[#6B6FA3]">
                        Select one or more children to see your total.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 mb-4">
                        {selectedChildren.map((child) => (
                          <div key={child.id} className="flex justify-between items-center text-sm">
                            <span className="text-[#6B6FA3]">{child.name}'s profile</span>
                            <span className="font-semibold text-[#1F224B]">{formatUsd(prices[child.id] ?? 0)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-[#F8F6FF] rounded-2xl p-4 border border-[#8E7BFF]/10">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-[#1F224B]">Total</span>
                          <span className="text-2xl font-extrabold bg-gradient-to-r from-[#F7B26A] to-[#F76C8A] bg-clip-text text-transparent">
                            {formatUsd(totalCents)}
                          </span>
                        </div>
                        <p className="text-xs text-[#6B6FA3] mt-1">One-time payment · USD</p>
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="text-center text-red-600 text-sm font-semibold mt-4 bg-red-50 border border-red-200 rounded-xl p-3">
                      {error}
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    onClick={handleUnlock}
                    disabled={selectedChildren.length === 0 || isProcessing}
                    className={`w-full h-14 rounded-2xl font-bold text-lg shadow-lg transition-all mt-5 ${
                      selectedChildren.length === 0 || isProcessing
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#F7B26A] to-[#F76C8A] text-white hover:brightness-105 btn-bouncy'
                    }`}
                  >
                    {isProcessing ? 'Redirecting to secure checkout...' : ctaLabel}
                  </button>

                  {/* Trust indicators */}
                  <div className="flex items-center justify-center gap-3 mt-3 text-xs text-[#6B6FA3] flex-wrap">
                    <span className="flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5 text-[#4C9E8F]" />
                      Secure
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-3.5 h-3.5 text-[#4C9E8F]" />
                      One-time
                    </span>
                    <span className="flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5 text-[#8E7BFF]" />
                      Stripe
                    </span>
                  </div>
                </Card>

                {/* Promo code — separate card, teal theme to distinguish from the
                    orange payment flow. Placed right after the payment button so
                    it's visible without scrolling. */}
                <Card className="p-4 rounded-3xl bg-gradient-to-br from-[#F0FBF7] to-[#F5F3FF] backdrop-blur border-2 border-[#4C9E8F]/20 shadow-md">
                  <div className="flex items-center gap-2 mb-1">
                    <Ticket className="w-4 h-4 text-[#4C9E8F]" />
                    <span className="text-sm font-bold text-[#1F224B]">Have a promo code?</span>
                  </div>
                  <p className="text-xs text-[#6B6FA3] mb-3">
                    Unlock one child for free — no payment needed. Each code works once.
                  </p>

                  {promoSuccess && (
                    <div className="mb-3 bg-green-50 border border-green-200 rounded-xl p-2.5 text-xs text-green-700 font-semibold flex items-start gap-2">
                      <Check className="w-4 h-4 flex-none mt-0.5" />
                      <span>{promoSuccess}</span>
                    </div>
                  )}
                  {promoError && (
                    <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs text-red-600 font-semibold">
                      {promoError}
                    </div>
                  )}

                  {/* Target child — auto-targets the single selection.
                      Only shows a picker when 2+ children are selected, using
                      radio-style dots in teal to stay visually distinct from the
                      orange toggle chips in the grid above. */}
                  {selectedChildren.length === 0 ? (
                    <p className="text-xs text-[#6B6FA3] text-center py-1">
                      Select a child above first, then enter your code.
                    </p>
                  ) : (
                    <>
                      {selectedChildren.length > 1 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-[#4C9E8F] mb-1.5">Unlock for:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedChildren.map((child) => {
                              const isTarget = promoTargetId === child.id;
                              return (
                                <button
                                  key={child.id}
                                  type="button"
                                  onClick={() => setPromoTargetId(child.id)}
                                  disabled={promoRedeeming}
                                  className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border transition-all ${
                                    isTarget
                                      ? 'bg-[#4C9E8F] text-white border-transparent'
                                      : 'bg-white text-[#6B6FA3] border-[#EDE9FF] hover:border-[#4C9E8F]/40'
                                  }`}
                                >
                                  <span className={`w-2.5 h-2.5 rounded-full border-2 flex-none ${
                                    isTarget ? 'border-white bg-white' : 'border-[#C5C8E8] bg-transparent'
                                  }`} />
                                  {child.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {selectedChildren.length === 1 && (
                        <p className="text-xs text-[#6B6FA3] mb-2.5">
                          → Unlocks <span className="font-semibold text-[#1F224B]">{selectedChildren[0].name}</span>
                        </p>
                      )}

                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="DPP-XXXX-XXXX"
                          disabled={promoRedeeming}
                          className="flex-1 min-w-0 px-3 py-2.5 rounded-xl border-2 border-[#4C9E8F]/20 focus:border-[#4C9E8F] focus:outline-none transition-colors font-mono text-sm tracking-wider disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                        <button
                          onClick={handleRedeemPromo}
                          disabled={promoRedeeming || !promoCode.trim() || !promoTargetId}
                          className={`flex-none px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
                            promoRedeeming || !promoCode.trim() || !promoTargetId
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-[#4C9E8F] text-white hover:brightness-105 shadow-md'
                          }`}
                        >
                          {promoRedeeming ? '...' : 'Apply'}
                        </button>
                      </div>
                    </>
                  )}
                </Card>

                <button
                  onClick={() => navigate('/app/payment-history')}
                  className="w-full text-sm font-semibold text-[#8E7BFF] hover:text-[#1F224B] transition-colors"
                >
                  View payment history →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
